import { useCallback, useEffect, useMemo, useState, memo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { sortBy, isEqual } from 'lodash'
import { fetchTransactionCategoriesIfNeeded } from '@/features/reports/reports.slice'
import { transactionFilterToQueryUrlService } from '@/services/transactionFilterToQueryUrlService'
import { getTransactionCategoryOptions } from '@/features/reports/reports.selectors'
import { updateTransactionFilters } from '@/reducers/finances/transactionFiltersReducer'
import { fetchFilteredUserTransactions } from './transactions.slice'
import { usePrevious, useReselector } from '@/utils/sharedHooks'
import { selectFinancialAccounts } from '@/selectors/financeSelectors'
import { formatAccountNameWithInstitution } from './helpers'
import { Select, FilterToggle, DatePicker, Combobox } from '@/ui'
import {
  DeviceWidth,
  DeviceWidthCombo,
  useIsDeviceWidth,
} from '@/utils/deviceWidthHelpers'
import { getCurrentUser } from '@/selectors/user.selectors'
import { useAppDispatch } from '@/utils/typeHelpers'

interface Props {
  transactionsType?: 'expenses' | 'income'
  /** When true, hides the date picker + filter toggle row (used when they're rendered externally, e.g. in the tab bar) */
  hideDateFilterRow?: boolean
}

export const UserTransactionsFilter = memo(
  ({ transactionsType, hideDateFilterRow = false }: Props) => {
    const dispatch = useAppDispatch()
    const [searchParams, setSearchParams] = useSearchParams()
    const defaultCat = searchParams.get('categoryId')
    const paramStartDate = searchParams.get('startDate')
    const paramEndDate = searchParams.get('endDate')
    const categories = useReselector(
      getTransactionCategoryOptions,
      transactionsType,
      true
    )
    const transactionFilters = useReselector(
      (state) => state.transactionFilters
    )
    const prevTransactionFilters = usePrevious(transactionFilters)
    const financialAccounts = useReselector(selectFinancialAccounts)
    const isMobile = useIsDeviceWidth(DeviceWidth.mobile)
    const isMobileOrTablet = useIsDeviceWidth(DeviceWidthCombo.mobileOrTablet)
    const currentUser = useReselector(getCurrentUser)
    const reduxShowFilters = useReselector(
      (state) => state.transactionFilters.showFilters ?? false
    )
    const [localShowFilters, setLocalShowFilters] = useState(reduxShowFilters)

    const handleShowFiltersChange = useCallback(
      (val: boolean) => {
        setLocalShowFilters(val)
        dispatch(updateTransactionFilters({ showFilters: val }))
      },
      [dispatch]
    )

    useEffect(() => {
      setLocalShowFilters(reduxShowFilters)
    }, [reduxShowFilters])

    useEffect(() => {
      // dispatch(
      //   clearTransactionFilters({ limit: isMobileOrTablet ? '10' : '100' })
      // )
      if (defaultCat) {
        dispatch(
          updateTransactionFilters({ transactionCategoryId: defaultCat })
        )
      }
      if (paramStartDate) {
        dispatch(updateTransactionFilters({ startDate: paramStartDate }))
      }
      if (paramEndDate) {
        dispatch(updateTransactionFilters({ endDate: paramEndDate }))
      }
    }, [defaultCat, dispatch, isMobileOrTablet, paramEndDate, paramStartDate])

    const fADropdownOptions = useMemo(() => {
      return sortBy(
        Object.values(financialAccounts).map((account) => ({
          text: formatAccountNameWithInstitution(account),
          value: String(account.id),
        })),
        'text'
      )
    }, [financialAccounts])

    useEffect(() => {
      dispatch(fetchTransactionCategoriesIfNeeded())
    }, [dispatch])

    const fetchTransactions = useCallback(() => {
      if (currentUser?.id) {
        dispatch(
          fetchFilteredUserTransactions(
            transactionFilterToQueryUrlService({
              ...transactionFilters,
              userId: currentUser.id,
            })
          )
        )
      }
    }, [dispatch, transactionFilters, currentUser?.id])

    useEffect(() => {
      if (!prevTransactionFilters) return
      if (!isEqual(transactionFilters, prevTransactionFilters)) {
        fetchTransactions()
      }
    }, [transactionFilters, prevTransactionFilters, fetchTransactions])

    const handleDateRangeChange = useCallback(
      ([startDate, endDate]: [string, string]) => {
        searchParams.delete('startDate')
        searchParams.delete('endDate')
        setSearchParams(searchParams)
        dispatch(updateTransactionFilters({ startDate, endDate }))
      },
      [dispatch, searchParams, setSearchParams]
    )

    const {
      startDate,
      endDate,
      transactionCategoryId,
      // transactionsType: filterType,
      financialAccountId,
    } = transactionFilters

    return (
      <div className="mb-6">
        {!hideDateFilterRow && (
          <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="w-full md:w-5/12 lg:max-w-[316px] [&_input]:text-sm">
              <DatePicker
                value={
                  !startDate && !endDate
                    ? ['', '']
                    : [startDate || '', endDate || '']
                }
                onChange={handleDateRangeChange}
                startDate={startDate || undefined}
                endDate={endDate || undefined}
                selectsRange
                clearable
                fullWidth
                placeholder="Filter by date"
                portalId="root"
              />
            </div>
            <div className="w-full sm:w-1/2 md:w-1/4 sm:text-right">
              <FilterToggle
                pressed={localShowFilters}
                onPressedChange={handleShowFiltersChange}
              >
                {' '}
                Filters
              </FilterToggle>
            </div>
          </div>
        )}

        {localShowFilters && (
          <div
            className={`mt-0 ${
              isMobile ? 'flex flex-col gap-3' : 'flex flex-wrap gap-4'
            }`}
          >
            <div className={isMobile ? '' : 'pr-0'}>
              <Combobox
                options={categories.map((category) => ({
                  value: String(category.value),
                  label: String(category.text),
                }))}
                value={
                  transactionCategoryId
                    ? String(transactionCategoryId)
                    : undefined
                }
                onValueChange={(val) =>
                  dispatch(
                    updateTransactionFilters({ transactionCategoryId: val })
                  )
                }
                placeholder="Filter by Category"
                widthClassName="w-full"
              />
            </div>
            <div className={isMobile ? '' : 'pr-0'}>
              <Select
                placeholder="Filter by Account"
                value={
                  financialAccountId ? String(financialAccountId) : undefined
                }
                options={fADropdownOptions}
                onValueChange={(value) =>
                  dispatch(
                    updateTransactionFilters({
                      financialAccountId: Number(value),
                    })
                  )
                }
                clearable
              />
            </div>
          </div>
        )}
      </div>
    )
  }
)

UserTransactionsFilter.displayName = 'UserTransactionsFilter'
